import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { UserListItem, UserRolePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  UserPlus, Pencil, Trash2, KeyRound, X, UserCheck,
} from "lucide-react";
import { PreviewPane, PreviewField } from "@/components/preview-pane";
import { MultiSelectDropdown } from "@/components/multi-select-dropdown";
import { useGridPreferences } from "@/lib/use-grid-preferences";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";

// --- Types ---

export interface RoleOption {
  value: string;
  label: string;
  scopeType: string;
}

// Single source of truth for all role options across user management pages
export const ALL_ROLE_OPTIONS: RoleOption[] = [
  { value: "coach", label: "Coach", scopeType: "school" },
  { value: "org_admin", label: "Organization Admin", scopeType: "organization" },
  { value: "parent", label: "Parent", scopeType: "school" },
  { value: "player", label: "Player", scopeType: "school" },
  { value: "school_admin", label: "School Admin", scopeType: "school" },
  { value: "sponsor_admin", label: "Sponsor Admin", scopeType: "sponsor" },
  { value: "sponsor_user", label: "Sponsor User", scopeType: "sponsor" },
  { value: "super_admin", label: "Super Admin", scopeType: "platform" },
  { value: "super_parent", label: "Super Parent", scopeType: "school" },
];

export interface UserManagementPanelProps {
  title: string;
  subtitle: string;
  scopeType: "platform" | "organization" | "school" | "sponsor";
  scopeId?: string;
  fetchUsers: () => Promise<{ users: UserListItem[] }>;
  assignableRoles: RoleOption[];
  canManage: boolean;
  showDetailLink?: boolean;
  filterUsers?: (users: UserListItem[]) => UserListItem[];
  /** When provided, shows a bookmark button on each row for persona switching. */
  onAddPersona?: (user: UserListItem) => void;
  isPersonaSaved?: (userId: string) => boolean;
  /** Opaque signature that changes when an external filter on `filterUsers`
   *  (e.g. the active pin scope) changes, so the table refetches. */
  listKey?: string;
}

// --- Helpers ---

const roleLabels: Record<string, string> = {
  super_admin: "Super Admin",
  org_admin: "Org Admin",
  school_admin: "School Admin",
  coach: "Coach",
  super_parent: "Super Parent",
  parent: "Parent",
  player: "Player",
  sponsor_admin: "Sponsor Admin",
  sponsor_user: "Sponsor User",
};

function formatName(firstName: string | null, lastName: string | null): string {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "--";
}

// --- Component ---

export function UserManagementPanel({
  title,
  subtitle,
  scopeType,
  scopeId,
  fetchUsers,
  assignableRoles,
  canManage,
  showDetailLink = false,
  filterUsers,
  onAddPersona,
  isPersonaSaved,
  listKey = "",
}: UserManagementPanelProps) {
  // User list state
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<UserListItem | null>(null);
  const [pinned, setPinned] = useState(false);
  // Bumped after every (re)load so the in-memory DataTable fetcher refreshes
  // even when the row count is unchanged (e.g. a name edit).
  const [listTick, setListTick] = useState(0);

  // Add user state
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addFirstName, setAddFirstName] = useState("");
  const [addLastName, setAddLastName] = useState("");
  // 078: blank by default -- force admin to make an explicit choice
  const [addRoleType, setAddRoleType] = useState("");
  const [addScopeId, setAddScopeId] = useState(scopeId ?? "");
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<{ created: boolean; tempPassword?: string } | null>(null);

  // Scope selector options (loaded once from server)
  const [orgs, setOrgs] = useState<Array<{ id: string; name: string }>>([]);
  const [schools, setSchools] = useState<Array<{ id: string; name: string }>>([]);
  const [sponsors, setSponsors] = useState<Array<{ id: string; name: string }>>([]);

  // Edit user state
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editRoleType, setEditRoleType] = useState("");
  const [editOriginalRoleType, setEditOriginalRoleType] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Reset password state
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Load users
  const loadUsers = useCallback(() => {
    setLoading(true);
    fetchUsers()
      .then((res) => {
        setUsers(res.users);
        setError(null);
        setListTick((t) => t + 1);
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load users"),
      )
      .finally(() => setLoading(false));
  }, [fetchUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load scope options once from server
  const selectedRole = assignableRoles.find((r) => r.value === addRoleType);
  const addScopeType = selectedRole?.scopeType ?? scopeType;
  const needsScopeSelector = scopeType === "platform" || (scopeType === "organization" && addScopeType !== "organization");

  useEffect(() => {
    if (!needsScopeSelector) return;
    api.getManagedScopes().then((res) => {
      setOrgs(res.organizations);
      setSchools(res.schools);
      setSponsors(res.sponsors);
      // Auto-select scope when only one option exists
      const role = assignableRoles.find((r) => r.value === addRoleType);
      const st = role?.scopeType ?? scopeType;
      if (st === "organization" && res.organizations.length === 1) {
        setAddScopeId(res.organizations[0].id);
      } else if (st === "school" && res.schools.length === 1) {
        setAddScopeId(res.schools[0].id);
      } else if (st === "sponsor" && res.sponsors.length === 1) {
        setAddScopeId(res.sponsors[0].id);
      }
    }).catch(() => {});
  }, [needsScopeSelector]);

  // Resolve scope ID for add form submission
  function resolveAddScopeId(): string {
    if (addScopeType === "platform") return "platform";
    if (needsScopeSelector) return addScopeId;
    return scopeId ?? "";
  }

  // --- Handlers ---

  function closeAllPanels() {
    setShowAdd(false);
    setAddError(null);
    setAddSuccess(null);
    setEditUserId(null);
    setEditError(null);
    setResetUserId(null);
    setResetResult(null);
    setResetError(null);
  }

  async function handleAddUser() {
    setAddError(null);
    setAddSubmitting(true);

    // 078: role must be explicitly chosen -- no silent default.
    if (!addRoleType) {
      setAddError("Please select a role");
      setAddSubmitting(false);
      return;
    }

    const finalScopeId = resolveAddScopeId();
    if (!finalScopeId) {
      setAddError("Please select a school / scope for this role");
      setAddSubmitting(false);
      return;
    }

    if (!addEmail.trim()) {
      setAddError("Email is required");
      setAddSubmitting(false);
      return;
    }

    try {
      const result = await api.createUser({
        email: addEmail,
        first_name: addFirstName,
        last_name: addLastName,
        role_type: addRoleType as "super_admin" | "org_admin" | "school_admin" | "coach" | "super_parent" | "parent" | "player" | "sponsor_admin" | "sponsor_user",
        scope_type: addScopeType as "platform" | "organization" | "school" | "sponsor",
        scope_id: finalScopeId,
      });
      setAddSuccess({
        created: result.created,
        tempPassword: result.temporary_password,
      });
      loadUsers();
    } catch (e) {
      setAddError(e instanceof api.ApiError ? e.message : "Failed to add user");
    } finally {
      setAddSubmitting(false);
    }
  }

  function autoSelectScope(newScopeType: string) {
    if (!needsScopeSelector) return;
    if (newScopeType === "organization" && orgs.length === 1) {
      setAddScopeId(orgs[0].id);
    } else if (newScopeType === "school" && schools.length === 1) {
      setAddScopeId(schools[0].id);
    } else if (newScopeType === "sponsor" && sponsors.length === 1) {
      setAddScopeId(sponsors[0].id);
    } else {
      setAddScopeId(scopeId ?? "");
    }
  }

  function handleAddRoleTypeChange(value: string) {
    setAddRoleType(value);
    const role = assignableRoles.find((r) => r.value === value);
    autoSelectScope(role?.scopeType ?? scopeType);
  }

  function resetAddForm() {
    setAddEmail("");
    setAddFirstName("");
    setAddLastName("");
    const newRole = assignableRoles[0]?.value ?? "";
    setAddRoleType(newRole);
    // Re-apply auto-selection logic (scopeId may be undefined on platform page)
    const role = assignableRoles.find((r) => r.value === newRole);
    const st = role?.scopeType ?? scopeType;
    if (scopeId) {
      setAddScopeId(scopeId);
    } else if (st === "organization" && orgs.length === 1) {
      setAddScopeId(orgs[0].id);
    } else if (st === "school" && schools.length === 1) {
      setAddScopeId(schools[0].id);
    } else if (st === "sponsor" && sponsors.length === 1) {
      setAddScopeId(sponsors[0].id);
    } else {
      setAddScopeId("");
    }
    setAddSuccess(null);
    setAddError(null);
  }

  function startEdit(u: UserListItem) {
    closeAllPanels();
    setEditUserId(u.user_id);
    setEditFirstName(u.first_name ?? "");
    setEditLastName(u.last_name ?? "");
    // Find the user's role at the current scope (for non-platform scopes)
    const scopeRole = u.roles.find(
      (r) => scopeType === "platform" || (r.scope_type === scopeType && r.scope_id === scopeId),
    );
    const currentRole = scopeRole?.role_type ?? assignableRoles[0]?.value ?? "";
    setEditRoleType(currentRole);
    setEditOriginalRoleType(currentRole);
  }

  async function handleSaveEdit() {
    if (!editUserId) return;
    setEditError(null);
    setEditSubmitting(true);
    try {
      const editUser = users.find((u) => u.user_id === editUserId);

      // Update name
      await api.updateUser(editUserId, {
        first_name: editFirstName,
        last_name: editLastName,
      });

      // Handle role change (non-platform scopes only)
      const roleChanged = editRoleType !== editOriginalRoleType;
      if (roleChanged && editUser && scopeType !== "platform") {
        // Remove old role(s) at this scope
        const oldRoles = editUser.roles.filter(
          (r) => r.scope_type === scopeType && r.scope_id === scopeId,
        );
        for (const role of oldRoles) {
          await api.removeUserRole(editUserId, role.id);
        }
        // Add new role at this scope
        await api.createUser({
          email: editUser.email,
          first_name: editFirstName,
          last_name: editLastName,
          role_type: editRoleType as "super_admin" | "org_admin" | "school_admin" | "coach" | "super_parent" | "parent" | "player" | "sponsor_admin" | "sponsor_user",
          scope_type: scopeType as "platform" | "organization" | "school" | "sponsor",
          scope_id: scopeId!,
        });
      }

      setEditUserId(null);
      loadUsers();
    } catch (e) {
      setEditError(e instanceof api.ApiError ? e.message : "Failed to update user");
    } finally {
      setEditSubmitting(false);
    }
  }

  function startResetPassword(userId: string) {
    closeAllPanels();
    setResetUserId(userId);
  }

  async function handleResetPassword() {
    if (!resetUserId) return;
    setResetError(null);
    setResetSubmitting(true);
    try {
      const result = await api.resetUserPassword(resetUserId);
      setResetResult(result.temporary_password);
    } catch (e) {
      setResetError(e instanceof api.ApiError ? e.message : "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  }

  async function handleRemoveUser(u: UserListItem) {
    if (scopeType === "platform") {
      if (!confirm("Delete this user? This action cannot be undone.")) return;
      try {
        await api.deleteUser(u.user_id);
        setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
        if (selected?.user_id === u.user_id) setSelected(null);
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to delete user");
      }
    } else {
      if (!confirm("Remove this user from this scope?")) return;
      // Remove all roles matching the current scope
      const scopeRoles = u.roles.filter(
        (r) => r.scope_type === scopeType && r.scope_id === scopeId,
      );
      try {
        for (const role of scopeRoles) {
          await api.removeUserRole(u.user_id, role.id);
        }
        loadUsers();
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to remove user");
      }
    }
  }

  // --- Role filter (URL-persisted) + shared DataTable list ---

  const [searchParams, setSearchParams] = useSearchParams();
  useGridPreferences("users");
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");

  const rolesParam = searchParams.get("roles") ?? "";
  const activeRoles = useMemo(
    () => (rolesParam ? new Set(rolesParam.split(",")) : new Set<string>()),
    [rolesParam],
  );

  // Update search params without adding a history entry. Search/sort/page are
  // owned by <DataTable> (q/sort/order/page); the role filter is the one extra
  // list param this panel manages.
  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }

  function toggleRole(role: string) {
    const next = new Set(activeRoles);
    if (next.has(role)) next.delete(role);
    else next.add(role);
    updateParams({ roles: next.size > 0 ? [...next].join(",") : null, page: null });
  }

  // The users list is fetched once into `users` (the add/edit/reset forms read
  // from it); the DataTable fetches from that in-memory set and applies the
  // page-local pin filter (filterUsers) + role filter + search/sort/paging.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: UserListItem[]; total: number }> => {
      let rows = filterUsers ? filterUsers(users) : users;
      if (activeRoles.size > 0) {
        rows = rows.filter((u) => u.roles.some((r) => activeRoles.has(r.role_type)));
      }
      return applyClientListParams(rows, params, {
        searchFields: (u) => [
          u.email,
          u.first_name,
          u.last_name,
          ...u.roles.map((r) => roleLabels[r.role_type] ?? r.role_type),
        ],
        sortAccessors: {
          email: (u) => u.email,
          name: (u) => formatName(u.first_name, u.last_name),
          roles: (u) => u.roles.map((r) => roleLabels[r.role_type] ?? r.role_type).sort().join(","),
        },
        defaultSort: "email",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [users, filterUsers, rolesParam, listKey],
  );

  const columns: DataTableColumn<UserListItem>[] = [
    {
      key: "email", header: "Email", sortable: true, className: "font-medium",
      render: (u) =>
        showDetailLink ? (
          <Link to={`/users/${u.user_id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
            {u.email}
          </Link>
        ) : (
          u.email
        ),
    },
    {
      key: "name", header: "Name", sortable: true, className: "text-muted-foreground",
      render: (u) => formatName(u.first_name, u.last_name),
    },
    {
      key: "roles", header: "Roles", sortable: true,
      render: (u) => (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap gap-1">
            {u.roles.map((role: UserRolePublic) => (
              <Badge key={role.id} variant="secondary" className="text-xs">
                {roleLabels[role.role_type] ?? role.role_type}
                {role.scope_name && ` @ ${role.scope_name}`}
              </Badge>
            ))}
          </div>
          {u.context && (
            <span className="text-xs text-muted-foreground">
              {[
                u.context.team_count !== undefined &&
                  `${u.context.team_count} team${u.context.team_count !== 1 ? "s" : ""}`,
                u.context.child_count !== undefined &&
                  `${u.context.child_count} child${u.context.child_count !== 1 ? "ren" : ""}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </div>
      ),
    },
  ];

  if (canManage || onAddPersona) {
    columns.push({
      key: "actions", header: "", headerClassName: "w-28",
      render: (u) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {onAddPersona && (
            <Button
              variant="ghost"
              size="sm"
              data-action="add-persona"
              data-state={isPersonaSaved?.(u.user_id) ? "saved" : "unsaved"}
              title={isPersonaSaved?.(u.user_id) ? "Already in personas" : "Add to persona switcher"}
              onClick={() => onAddPersona(u)}
              className={isPersonaSaved?.(u.user_id) ? "text-amber-600" : ""}
            >
              <UserCheck className="h-4 w-4" />
            </Button>
          )}
          {canManage && (
            <>
              <Button variant="ghost" size="sm" title="Edit name" onClick={() => startEdit(u)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" title="Reset password" onClick={() => startResetPassword(u.user_id)}>
                <KeyRound className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title={scopeType === "platform" ? "Delete user" : "Remove from scope"}
                onClick={() => handleRemoveUser(u)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </>
          )}
        </div>
      ),
    });
  }

  // --- Render ---

  return (
    <div className="flex h-full gap-0">
    <div className="min-w-0 flex-1 flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
        {canManage && (
          <Button
            variant="outline"
            onClick={() => {
              closeAllPanels();
              setShowAdd(true);
              resetAddForm();
            }}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Add user card */}
      {showAdd && !addSuccess && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add User</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              {addError && <p className="text-sm text-destructive">{addError}</p>}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label htmlFor="add-email">Email</Label>
                  <Input
                    id="add-email"
                    type="email"
                    placeholder="user@example.com"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="add-firstName">First Name</Label>
                  <Input
                    id="add-firstName"
                    placeholder="First name"
                    value={addFirstName}
                    onChange={(e) => setAddFirstName(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="add-lastName">Last Name</Label>
                  <Input
                    id="add-lastName"
                    placeholder="Last name"
                    value={addLastName}
                    onChange={(e) => setAddLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="add-roleType">Role *</Label>
                <select
                  id="add-roleType"
                  value={addRoleType}
                  onChange={(e) => handleAddRoleTypeChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  required
                >
                  <option value="" disabled>
                    Select a role…
                  </option>
                  {assignableRoles.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Scope selector -- only for platform and organization scopes */}
              {needsScopeSelector && addScopeType === "organization" && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="add-scopeId">Organization</Label>
                  <select
                    id="add-scopeId"
                    value={addScopeId}
                    onChange={(e) => setAddScopeId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {orgs.length !== 1 && <option value="">Select organization...</option>}
                    {orgs.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {needsScopeSelector && addScopeType === "school" && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="add-scopeId">School</Label>
                  <select
                    id="add-scopeId"
                    value={addScopeId}
                    onChange={(e) => setAddScopeId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {schools.length !== 1 && <option value="">Select school...</option>}
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {needsScopeSelector && addScopeType === "sponsor" && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="add-scopeId">Sponsor</Label>
                  <select
                    id="add-scopeId"
                    value={addScopeId}
                    onChange={(e) => setAddScopeId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {sponsors.length !== 1 && <option value="">Select sponsor...</option>}
                    {sponsors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={addSubmitting || !addEmail || !addFirstName || !addLastName}
                  onClick={handleAddUser}
                >
                  {addSubmitting ? "Adding..." : "Add"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowAdd(false);
                    resetAddForm();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add success card */}
      {showAdd && addSuccess && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2">
              <p className="font-medium text-green-800 dark:text-green-200">
                {addSuccess.created
                  ? "User created and role assigned successfully!"
                  : "Role assigned to existing user successfully!"}
              </p>
              {addSuccess.tempPassword && (
                <div className="rounded-md bg-white p-3 dark:bg-gray-900">
                  <p className="text-sm text-muted-foreground">Temporary password:</p>
                  <p className="font-mono text-lg font-bold">{addSuccess.tempPassword}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Share this with the user securely. They should change it on first login.
                  </p>
                </div>
              )}
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={resetAddForm}>
                  Add Another
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowAdd(false);
                    resetAddForm();
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit user card */}
      {editUserId && (() => {
        const editUser = users.find((u) => u.user_id === editUserId);
        if (!editUser) return null;
        return (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Edit User -- {editUser.email}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditUserId(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3">
                {editError && <p className="text-sm text-destructive">{editError}</p>}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="edit-firstName">First Name</Label>
                    <Input
                      id="edit-firstName"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="edit-lastName">Last Name</Label>
                    <Input
                      id="edit-lastName"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                    />
                  </div>
                </div>
                {scopeType !== "platform" && assignableRoles.length > 1 && (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="edit-roleType">Role</Label>
                    <select
                      id="edit-roleType"
                      aria-label="Role"
                      value={editRoleType}
                      onChange={(e) => setEditRoleType(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {assignableRoles.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" disabled={editSubmitting || !editFirstName || !editLastName} onClick={handleSaveEdit}>
                    {editSubmitting ? "Saving..." : "Save"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditUserId(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Reset password card */}
      {resetUserId && (() => {
        const resetUser = users.find((u) => u.user_id === resetUserId);
        if (!resetUser) return null;
        return (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Reset Password -- {resetUser.email}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setResetUserId(null);
                  setResetResult(null);
                  setResetError(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3">
                {resetError && <p className="text-sm text-destructive">{resetError}</p>}
                {!resetResult && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      This will generate a new temporary password for the user. They will need to use it to sign in.
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" disabled={resetSubmitting} onClick={handleResetPassword}>
                        {resetSubmitting ? "Resetting..." : "Reset Password"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setResetUserId(null);
                          setResetError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                )}
                {resetResult && (
                  <div className="rounded-md bg-white p-3 dark:bg-gray-900">
                    <p className="text-sm text-muted-foreground">New temporary password:</p>
                    <p className="font-mono text-lg font-bold">{resetResult}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Share this with the user securely. They should change it on first login.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => {
                        setResetUserId(null);
                        setResetResult(null);
                      }}
                    >
                      Done
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* List */}
      {loading && users.length === 0 ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <div data-region="results" data-state={tableState}>
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={`${listKey}:${rolesParam}:${users.length}:${listTick}`}
            rowKey={(u) => u.user_id}
            rowProps={(u) => ({ "data-row-user-id": u.user_id, "data-row-user-email": u.email })}
            searchPlaceholder="Search users..."
            defaultSort="email"
            emptyMessage="No users found."
            toolbarExtra={
              assignableRoles.length > 1 ? (
                <MultiSelectDropdown
                  label="Roles"
                  options={assignableRoles.map((r) => ({ value: r.value, label: r.label }))}
                  selected={activeRoles}
                  onToggle={toggleRole}
                  onClear={() => updateParams({ roles: null, page: null })}
                />
              ) : undefined
            }
            onRowClick={(u) => setSelected(u)}
            rowClassName={(u) => (selected?.user_id === u.user_id ? "bg-muted" : undefined)}
            onStateChange={setTableState}
          />
        </div>
      )}
    </div>
    <PreviewPane pinned={pinned} onTogglePin={() => setPinned(p => !p)} title="User">
      {selected && (
        <>
          <PreviewField label="Email">{selected.email}</PreviewField>
          <PreviewField label="Name">{formatName(selected.first_name, selected.last_name)}</PreviewField>
          <PreviewField label="Roles">
            <div className="flex flex-wrap gap-1">
              {selected.roles.map((role: UserRolePublic) => (
                <Badge key={role.id} variant="secondary" className="text-xs">
                  {roleLabels[role.role_type] ?? role.role_type}
                  {role.scope_name && ` @ ${role.scope_name}`}
                </Badge>
              ))}
            </div>
          </PreviewField>
          {selected.context && (
            <PreviewField label="Context">
              {[
                selected.context.team_count !== undefined &&
                  `${selected.context.team_count} team${selected.context.team_count !== 1 ? "s" : ""}`,
                selected.context.child_count !== undefined &&
                  `${selected.context.child_count} child${selected.context.child_count !== 1 ? "ren" : ""}`,
              ]
                .filter(Boolean)
                .join(" · ") || "--"}
            </PreviewField>
          )}
        </>
      )}
    </PreviewPane>
    </div>
  );
}
